import React from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import FormHelperText from '@material-ui/core/FormHelperText'
import Typography from '@material-ui/core/Typography'
import { DateTime } from 'luxon'
import { LocalizationProvider, DesktopTimePicker } from '@mui/x-date-pickers'
import { ThemeProvider } from '@mui/material/styles'
import CustomLuxonAdapter from 'lib/CustomLuxonAdapter'
import { outlinedPickerFieldSx } from 'lib/form-field-styles'
import AccessTimeIcon from 'components/material-symbol/icons/AccessTime'
import PgaKeyboardDatePicker from 'components/pga-time-picker/pga-keyboard-date-picker'
import { mergeDesktopDatePickerSlotProps } from 'components/pga-time-picker/desktop-date-picker'
import { withEnterTimePicker } from 'components/pga-time-picker/enter-time-view'
import { calendarDatePickerTheme } from 'pages/pga-coach/bookings/schedule/calendar/calendar-date-picker-theme'
import ChatPreview from '../chat-preview'
import { LOCAL_INPUT_FORMAT, MAX_SCHEDULE_DAYS } from '../constants'
import { pluralize } from '../format'
import useStyles from '../styles'
import ChoiceRows from './choice-rows'
import FormSection from './form-section'
import ScheduleIcon from 'components/material-symbol/icons/Schedule'
import SendIcon from 'components/material-symbol/icons/Send'
import EditIcon from 'components/material-symbol/icons/Edit'
import WarningIcon from 'components/material-symbol/icons/Warning'
import TipWell, { TipWellActionButton } from 'components/tip-well'
import { findPlaceholders } from './validation'

const PICKER_FIELD_HEIGHT = 56
const PICKER_ICON_COLOR = '#172024'

const pickerTextField = {
  variant: 'outlined',
  fullWidth: true,
  sx: {
    ...outlinedPickerFieldSx,
    '& .MuiOutlinedInput-root': {
      ...outlinedPickerFieldSx['& .MuiOutlinedInput-root'],
      height: PICKER_FIELD_HEIGHT,
    },
  },
}

const pickerIconButton = { sx: { color: PICKER_ICON_COLOR } }

const DELIVERY_OPTIONS = [
  {
    value: 'now',
    title: 'Send now',
    description: 'Students get it right away.',
    icon: SendIcon,
  },
  {
    value: 'later',
    title: 'Schedule for later',
    description: `Pick a date and time up to ${MAX_SCHEDULE_DAYS} days from now.`,
    icon: ScheduleIcon,
  },
]

export default function ReviewStep({
  campaign,
  audienceCount,
  delivery,
  scheduledFor,
  minScheduledFor,
  maxScheduledFor,
  scheduleError,
  onDeliveryChange,
  onScheduledForChange,
  onEditMessage,
}) {
  const classes = useStyles()
  const placeholders = findPlaceholders(campaign.body)
  const parsed = DateTime.fromFormat(scheduledFor, LOCAL_INPUT_FORMAT)
  const scheduledAt = parsed.isValid ? parsed : null
  const minDate = DateTime.fromFormat(minScheduledFor, LOCAL_INPUT_FORMAT)
  const maxDate = DateTime.fromFormat(maxScheduledFor, LOCAL_INPUT_FORMAT)

  const change = (picked, parts) => {
    if (!picked?.isValid) return
    const base = scheduledAt || minDate
    onScheduledForChange(base.set(parts(picked)).toFormat(LOCAL_INPUT_FORMAT))
  }
  const changeDate = picked =>
    change(picked, ({ year, month, day }) => ({ year, month, day }))
  const changeTime = picked =>
    change(picked, ({ hour, minute }) => ({ hour, minute }))

  return (
    <>
      <FormSection
        title="What students see"
        description={[
          campaign.name,
          `Goes to ${pluralize(audienceCount, 'student')}`,
          campaign.offerings.length > 0
            ? pluralize(campaign.offerings.length, 'offering')
            : null,
          campaign.media.length > 0
            ? pluralize(campaign.media.length, 'attachment')
            : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      >
        <Box className={classes.listStack}>
          <ChatPreview
            body={campaign.body}
            offerings={campaign.offerings}
            media={campaign.media}
          />
          {placeholders.length > 0 ? (
            <TipWell
              variant="warning"
              icon={WarningIcon}
              caption={`Your message still has placeholders to fill in: ${placeholders.join(
                ', ',
              )}. You can still send it as is.`}
              action={
                <TipWellActionButton
                  variant="outlined"
                  aria-label="Edit Message"
                  startIcon={<EditIcon />}
                  onClick={onEditMessage}
                >
                  Edit
                </TipWellActionButton>
              }
            />
          ) : null}
          {campaign.offerings.length > 0 ? (
            <Typography className={classes.helperText}>
              Bookings made from these cards count as “From link.” Bookings from
              recipients within 14 days that didn’t use a card count separately
              as “Booked later.”
            </Typography>
          ) : null}
        </Box>
      </FormSection>

      <FormSection title="When to send" titleId="campaign-delivery-title">
        <Box className={classes.sectionContent}>
          <ChoiceRows
            options={DELIVERY_OPTIONS}
            value={delivery}
            onChange={onDeliveryChange}
            labelledBy="campaign-delivery-title"
          />
          {delivery === 'later' ? (
            <Box>
              <ThemeProvider theme={calendarDatePickerTheme}>
                <LocalizationProvider
                  dateAdapter={CustomLuxonAdapter}
                  locale="en-us"
                >
                  <Box className={classes.dateTimeFields}>
                    <PgaKeyboardDatePicker
                      label="Date"
                      format="MMM d, yyyy"
                      value={scheduledAt}
                      onChange={changeDate}
                      minDate={minDate}
                      maxDate={maxDate}
                      slotProps={mergeDesktopDatePickerSlotProps({
                        textField: {
                          ...pickerTextField,
                          error: Boolean(scheduleError),
                        },
                        openPickerButton: pickerIconButton,
                      })}
                    />
                    <DesktopTimePicker
                      label="Time"
                      value={scheduledAt}
                      onChange={changeTime}
                      minutesStep={5}
                      slots={{ openPickerIcon: AccessTimeIcon }}
                      {...withEnterTimePicker({
                        textField: {
                          ...pickerTextField,
                          error: Boolean(scheduleError),
                        },
                        openPickerButton: pickerIconButton,
                      })}
                    />
                  </Box>
                </LocalizationProvider>
              </ThemeProvider>
              <FormHelperText error={Boolean(scheduleError)}>
                {scheduleError || 'Uses your computer’s time zone.'}
              </FormHelperText>
            </Box>
          ) : null}
        </Box>
      </FormSection>
    </>
  )
}

ReviewStep.propTypes = {
  campaign: PropTypes.shape({
    name: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    offerings: PropTypes.array.isRequired,
    media: PropTypes.array.isRequired,
  }).isRequired,
  audienceCount: PropTypes.number.isRequired,
  delivery: PropTypes.oneOf(['now', 'later']).isRequired,
  scheduledFor: PropTypes.string.isRequired,
  minScheduledFor: PropTypes.string.isRequired,
  maxScheduledFor: PropTypes.string.isRequired,
  scheduleError: PropTypes.string,
  onDeliveryChange: PropTypes.func.isRequired,
  onScheduledForChange: PropTypes.func.isRequired,
  onEditMessage: PropTypes.func.isRequired,
}
